import React from 'react';
import { useT } from '../../i18n';

// A row as one line, short enough to scan. Empty cells are shown as a dash
// rather than dropped: "which of these two is the empty one" is exactly the
// question someone checking a duplicate is asking.
export const rowLabel = (row) =>
  Object.values(row)
    .map(value => (value === null || value === '' ? '—' : String(value)))
    .join(' · ');

/**
 * Les groupes d'un plan de dédoublonnage : ce qui est gardé, ce qui part.
 *
 * Un seul rendu, parce que deux opérations montrent le même plan — le
 * dédoublonnage général et celui des adresses de courriel. Recopié, l'un des
 * deux aurait fini par dire autre chose que l'autre sur la même donnée.
 *
 * La ligne gardée est montrée à côté de celles qui partent, avec le reste de
 * la ligne : c'est ce qui distingue deux doublons l'un de l'autre.
 */
export const DuplicateGroups = ({ plan, className = 'max-h-64 overflow-y-auto space-y-3' }) => {
  const t = useT();
  if (!plan || plan.rows_removed === 0) return null;

  return (
    <div className={className} data-testid="duplicate-groups">
      {plan.groups.map((group, index) => (
        <div key={index} className="text-xs border-l-2 border-primary/40 pl-3">
          {group.key && (
            <div className="font-medium mb-1">
              {Object.entries(group.key)
                .map(([name, value]) => `${name} : ${value === null ? '—' : value}`)
                .join(' · ')}
            </div>
          )}
          <div className="text-muted-foreground">
            {t('duplicates.planKept', { row: rowLabel(group.kept) })}
          </div>
          <ul className="mt-0.5 space-y-0.5">
            {group.removed.map((row, position) => (
              <li key={position} className="text-destructive/90">
                {t('duplicates.planRemoved', { row: rowLabel(row) })}
              </li>
            ))}
            {group.removed_total > group.removed.length && (
              <li className="text-muted-foreground">
                {t('duplicates.planMoreRows', {
                  rows: group.removed_total - group.removed.length,
                })}
              </li>
            )}
          </ul>
        </div>
      ))}
      {plan.truncated && (
        <p className="text-muted-foreground">
          {t('duplicates.planTruncated', {
            shown: plan.groups.length, total: plan.groups_total,
          })}
        </p>
      )}
    </div>
  );
};

export default DuplicateGroups;
